// src/Components/ClubChat/ClubChat.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  limitToLast,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Storage } from '@capacitor/storage';
import { Capacitor } from '@capacitor/core';
import { auth, db, signInWithBackendToken } from '../../firebase';
import { getDistance } from '../../utils/geo';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import './ClubChat.css';

const loadKakaoMapScript = (() => {
  let promise;
  return (apiKey) => {
    if (promise) return promise;
    promise = new Promise((resolve, reject) => {
      if (document.querySelector(`script[src*="dapi.kakao.com"]`)) {
        if (window.kakao && window.kakao.maps) {
          resolve();
        } else {
          document
            .querySelector(`script[src*="dapi.kakao.com"]`)
            .addEventListener('load', resolve);
        }
        return;
      }
      const script = document.createElement('script');
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false&libraries=services`;
      script.async = true;
      script.onload = () => {
        if (window.kakao && window.kakao.maps) {
          window.kakao.maps.load(() => resolve());
        } else {
          reject(new Error('Kakao Maps SDK failed to load.'));
        }
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return promise;
  };
})();

const RADIUS_METERS = 100;


export default function ClubChat({ club }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [verified, setVerified] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const navigate = useNavigate();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { user: reduxUser, token } = useSelector((state) => state.auth);

  // Debug: initial mount
  useEffect(() => {
    console.log('🔍 ClubChat Mount:', {
      reduxUser,
      token,
      clubId: club?.id,
      isNative: Capacitor.isNativePlatform(),
    });
  }, [reduxUser, token, club]);

  // 1) Firebase auth listener → ensure Firebase user is signed in
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('👤 Firebase auth state changed:', {
        hasUser: !!firebaseUser,
        uid: firebaseUser?.uid,
        email: firebaseUser?.email,
        displayName: firebaseUser?.displayName,
      });

      if (!firebaseUser && reduxUser && token) {
        try {
          const userCredential = await signInWithBackendToken(token, reduxUser.name);
          setUser(userCredential.user);
        } catch (err) {
          console.error('Firebase sign-in error:', err);
          setError('Authentication error. Please log in again.');
          navigate('/login', {
            state: {
              error: 'Authentication error. Please log in again.',
              returnTo: `/clubs/${club.id}/chat`,
            },
          });
        }
      } else {
        setUser(firebaseUser);
      }
      setIsLoading(false);
    });
    return () => unsub();
  }, [reduxUser, token, navigate, club.id]);

  // 2) Real-time listener: Firestore messages with front-end filtering for reported messages
  useEffect(() => {
    if (!club || !club.id) return; // club.id가 유효한지 확인

    const q = query(
      collection(db, 'clubs', String(club.id), 'messages'),
      orderBy('createdAt', 'asc'),
      limitToLast(200) 
    );

    const unsub = onSnapshot(q, (snap) => {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

      const processedMsgs = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          // Firestore Timestamp를 JavaScript Date 객체로 변환하여 저장 (필터링 및 formatTime에서 사용 용이)
          createdAtDate: data.createdAt?.toDate() 
        };
      });

      const filteredMsgs = processedMsgs.filter((msg) => {
        if (!msg.createdAtDate) {
          // createdAtDate가 없으면 (예: createdAt 필드가 누락된 데이터), 일단 표시하지 않음
          // 또는 console.warn으로 로그를 남기고 표시할 수도 있음
          console.warn("Message without createdAtDate found:", msg.id);
          return false; 
        }

        if (msg.reported === true) {
          // 신고된 메시지: createdAtDate가 24시간 이내인 경우에만 표시
          return msg.createdAtDate > twentyFourHoursAgo;
        } else {
          // 일반 메시지: 항상 표시 (Firestore TTL이 이미 24시간 지난 것을 걸러줄 것으로 기대)
          // 만약 TTL에만 의존하지 않고 프론트엔드에서도 명시적으로 필터링하고 싶다면,
          // 여기에도 return msg.createdAtDate > twentyFourHoursAgo; 조건을 추가할 수 있습니다.
          return true; 
        }
      });

      setMsgs(filteredMsgs);
      // 화면 맨 아래로 스크롤 (새 메시지 또는 필터링 후에도)
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, (error) => {
      // 에러 핸들링 (예: 권한 문제 등)
      console.error("Error listening to messages: ", error);
      setError("메시지를 불러오는 중 오류가 발생했습니다.");
    });

    return () => unsub(); // cleanup a 리스너 해제
  }, [club.id]); // club.id가 변경될 때마다 리스너 재설정

  // 3) Geocode club.address → clubCoords
  const [clubCoords, setClubCoords] = useState(null);
  const geocoderRef = useRef(null);
  useEffect(() => {
    const apiKey = process.env.REACT_APP_KAKAO_MAP_API_KEY;
    if (!apiKey) return console.error('Missing Kakao API key');
    loadKakaoMapScript(apiKey)
      .then(() => {
        geocoderRef.current = new window.kakao.maps.services.Geocoder();
        geocoderRef.current.addressSearch(club.address, (res, status) => {
          if (status === window.kakao.maps.services.Status.OK && res[0]) {
            setClubCoords({ lat: +res[0].y, lng: +res[0].x });
          }
        });
      })
      .catch(console.error);
  }, [club.address]);

  // 4) One-time radius check
  useEffect(() => {
    if (!clubCoords) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const dist = getDistance(
          pos.coords.latitude,
          pos.coords.longitude,
          clubCoords.lat,
          clubCoords.lng
        );
        setVerified(dist <= RADIUS_METERS);
      },
      () => setVerified(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [clubCoords]);

  // 5) Send message via FastAPI endpoint
  const handleSend = async () => {
    if (!user) {
      setError('로그인 후 이용해주세요.');
      return;
    }

    const textToSend = input.trim();
    if (!textToSend) return;

    try {
      await api.post(
        `/api/v1/chat/clubs/${club.id}/messages`, 
        {
          text: textToSend,
          verified: verified,
        }
      );
      setInput('');
      setError(null);
    } catch (err) {
      console.error(
        'Message send API error:',
        err.response ? err.response.data : err.message,
        `Using URL path: /api/v1/chat/clubs/${club.id}/messages` 
      );
      setError(err.response?.data?.detail || '메시지 전송에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // Timestamp formatter: Firestore Timestamp (또는 JS Date) → "HH:mm"
  const formatTime = (dateObject) => { // 입력으로 JavaScript Date 객체를 받음
    if (!dateObject || !(dateObject instanceof Date)) return '';
    const hh = String(dateObject.getHours()).padStart(2, '0');
    const mm = String(dateObject.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // Add/remove body class when input is focused (keyboard styling)
  useEffect(() => {
    if (isInputFocused) {
      document.body.classList.add('keyboard-open');
    } else {
      document.body.classList.remove('keyboard-open');
    }
    return () => {
      document.body.classList.remove('keyboard-open');
    };
  }, [isInputFocused]);

  // "Back" button: preserve previous page state
  const handleGoBack = () => {
    navigate(-1, { state: { preserveState: true } });
  };

  // Function to show a toast message (using window.alert for now)
  const showToast = (message) => {
    window.alert(message); // 간단한 alert 사용, 필요시 더 나은 토스트 라이브러리/컴포넌트 사용
  };

  // Handler for reporting a message
  const handleReportMessage = async (messageId) => {
    if (!user) {
      showToast('신고하려면 로그인이 필요합니다.');
      return;
    }

    try {
      await api.post(
        `/api/v1/chat/clubs/${club.id}/messages/${messageId}/report`,
        {} // No request body needed for report
      );
      showToast('메시지가 신고되었습니다. 운영팀에서 확인 후 조치하겠습니다.');
    } catch (err) {
      console.error(
        'Report message API error:',
        err.response ? err.response.data : err.message,
        `Using URL path: /api/v1/chat/clubs/${club.id}/messages/${messageId}/report`
      );
      showToast(err.response?.data?.detail || '메시지 신고 중 오류가 발생했습니다.');
    }
  };

  if (isLoading) {
    return <div className="loading-message">Loading chat...</div>;
  }
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="chat-wrapper">
      <header className="chat-header">
        <button className="back-button" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <span className="header-title">{club.name}</span>
      </header>

      <ul className="chat-list">
        {msgs.map((m) => (
          <li
            key={m.id}
            className={`chat-message ${m.userId === user?.uid ? 'me' : ''} ${m.reported ? 'reported-message' : ''}`}
          >
            <div className="bubble-container">
              <div className="bubble-header">
                {m.userProfilePic ? (
                  <img
                    src={m.userProfilePic}
                    alt="Profile"
                    className="chat-profile-picture"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '';
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <FontAwesomeIcon icon={faUserCircle} className="chat-profile-icon" />
                )}
                <span className={`badge ${m.verified ? 'verified' : 'remote'}`}>
                  {m.verified ? (
                    <>
                      <span className="status-indicator" />
                      해당 클럽 인근
                    </>
                  ) : (
                    ' '
                  )}
                </span>
                <span className="user">{m.userName}</span>
              </div>
              <p className="bubble">{m.text}</p>
              <div className="message-meta">
                <span className="timestamp">{formatTime(m.createdAtDate)}</span>
                <div className="report-section">
                  {m.reported ? (
                    <span className="reported-status">신고됨</span>
                  ) : (
                    m.userId !== user?.uid && (
                      <button
                        className="report-button"
                        onClick={() => {
                          if (window.confirm(`"${m.text}" 메시지를 신고하시겠습니까?`)) {
                            handleReportMessage(m.id);
                          }
                        }}
                      >
                        신고
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
        <div ref={bottomRef} />
      </ul>

      <footer className="chat-footer">
        <input
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          placeholder="메세지를 입력하세요"
          type="text"
          enterkeyhint="send"
          autoCapitalize="sentences"
          autoComplete="off"
          spellCheck="true"
        />
        <button className="chat-send-button" onClick={handleSend}>
          전송
        </button>
      </footer>
    </div>
  );
}

if (module.hot) {
  module.hot.accept();
}
